import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/** A stage's zoom: scale, then translate, about the stage's top-left, in stage px. */
export interface ZoomState {
  s: number;
  tx: number;
  ty: number;
  /** The stage's size on screen when the state was written, so the composite can scale tx and ty. */
  w: number;
  h: number;
}

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 4;
export const IDENTITY_ZOOM: ZoomState = { s: 1, tx: 0, ty: 0, w: 0, h: 0 };

/** Pan limits: the scaled content always covers the stage, so no empty space shows. */
export function clampZoom(z: ZoomState): ZoomState {
  const s = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z.s));
  const minX = z.w - z.w * s;
  const minY = z.h - z.h * s;
  return { ...z, s, tx: Math.min(0, Math.max(minX, z.tx)), ty: Math.min(0, Math.max(minY, z.ty)) };
}

/**
 * Two-finger pinch about the pinch point (1x to 4x) and pan, applied as one GPU
 * transform on `targetRef` (spec 9.7). Touch events on `surfaceRef`, with
 * preventDefault only once a gesture is under way, so taps, scrolls and strokes
 * that are not gestures are left alone.
 *
 * - Two fingers: pinch and pan together (the midpoint drags the content).
 * - One finger pans while zoomed, but only when `onePanAllowed()` says so
 *   (the pen is off). With the pen on, one finger draws.
 * - `onMultiTouch` fires when a second finger lands, so a stroke the first
 *   finger began can be cancelled.
 *
 * The transform is written straight to the element each frame; React state
 * only carries the scale, for the Reset chip.
 */
export function usePinchZoom(opts: {
  enabled: boolean;
  surfaceRef: RefObject<HTMLElement | null>;
  targetRef: RefObject<HTMLElement | null>;
  /** Every change of zoom, for a reader outside React (the recorder's composite). */
  onChange?: (z: ZoomState) => void;
  onePanAllowed?: () => boolean;
  onMultiTouch?: () => void;
}): { scale: number; reset: () => void } {
  const { enabled, surfaceRef, targetRef } = opts;
  const [scale, setScale] = useState(1);
  const stateRef = useRef<ZoomState>({ ...IDENTITY_ZOOM });
  const optsRef = useRef(opts);
  useEffect(() => { optsRef.current = opts; });

  const apply = useCallback((z: ZoomState) => {
    stateRef.current = z;
    optsRef.current.onChange?.(z);
    const t = targetRef.current;
    if (t) {
      t.style.transformOrigin = '0 0';
      t.style.transform = z.s === 1 && z.tx === 0 && z.ty === 0 ? '' : `translate3d(${z.tx}px, ${z.ty}px, 0) scale(${z.s})`;
    }
  }, [stateRef, targetRef]);

  const reset = useCallback(() => {
    const t = targetRef.current;
    const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (t && !reduce) {
      t.style.transition = 'transform 200ms ease-out';
      setTimeout(() => { if (t) t.style.transition = ''; }, 220);
    }
    apply({ ...stateRef.current, s: 1, tx: 0, ty: 0 });
    setScale(1);
  }, [apply, stateRef, targetRef]);

  /* Spec 11.2: zoom is never stuck. The listeners follow whatever element is
     the surface now, not the one there when the hook first ran: a panel that
     re-creates the stage, or a stage that was not mounted yet, would
     otherwise leave pinch and pan bound to a detached node. Checked after
     every render; attaching is only redone when the element changes. */
  const attachedRef = useRef<{ el: HTMLElement | null; off: (() => void) | null }>({ el: null, off: null });
  const lastTargetRef = useRef<HTMLElement | null>(null);
  // Runs after every render on purpose (see above); setScale is only handed on.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = enabled ? surfaceRef.current : null;
    if (attachedRef.current.el !== el) {
      attachedRef.current.off?.();
      attachedRef.current = { el, off: el ? attachGestures(el, { apply, stateRef, optsRef, setScale }) : null };
    }
    /* A new target element (re-mounted) gets the current zoom, not 1x. */
    const t = targetRef.current;
    if (t && t !== lastTargetRef.current) {
      lastTargetRef.current = t;
      if (stateRef.current.s > 1.001) apply(stateRef.current);
    }
  });
  useEffect(() => () => { attachedRef.current.off?.(); attachedRef.current = { el: null, off: null }; }, []);


  return { scale, reset };
}

/** Pinch, pan and resize handling on one surface; returns the detach. */
function attachGestures(surface: HTMLElement, ctx: {
  apply: (z: ZoomState) => void;
  stateRef: { current: ZoomState };
  optsRef: { current: { onePanAllowed?: () => boolean; onMultiTouch?: () => void; onChange?: (z: ZoomState) => void } };
  setScale: (s: number) => void;
}): () => void {
  const { apply, stateRef, optsRef, setScale } = ctx;
  type Pinch = { d0: number; m0: { x: number; y: number }; z0: ZoomState };
  type Pan = { p0: { x: number; y: number }; z0: ZoomState };
  let pinch: Pinch | null = null;
  let pan: Pan | null = null;
  let raf = 0;
  let pending: ZoomState | null = null;
  const flush = () => { raf = 0; if (pending) { apply(pending); pending = null; } };
  const schedule = (z: ZoomState) => { pending = z; if (!raf) raf = requestAnimationFrame(flush); };
  const local = (t: Touch) => {
    const r = surface.getBoundingClientRect();
    return { x: t.clientX - r.left, y: t.clientY - r.top, w: r.width, h: r.height };
  };
  const startPinch = (e: TouchEvent) => {
    const a = local(e.touches[0]);
    const b = local(e.touches[1]);
    pinch = {
      d0: Math.hypot(a.x - b.x, a.y - b.y) || 1,
      m0: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      z0: { ...stateRef.current, w: a.w, h: a.h },
    };
    pan = null;
  };
  const startPan = (e: TouchEvent) => {
    const p = local(e.touches[0]);
    pan = { p0: { x: p.x, y: p.y }, z0: { ...stateRef.current, w: p.w, h: p.h } };
  };
  const onStart = (e: TouchEvent) => {
    if (e.touches.length >= 2) {
      e.preventDefault();
      optsRef.current.onMultiTouch?.();
      startPinch(e);
    } else if (e.touches.length === 1 && stateRef.current.s > 1.001 && (optsRef.current.onePanAllowed?.() ?? true)) {
      startPan(e);
    }
  };
  const onMove = (e: TouchEvent) => {
    if (pinch && e.touches.length >= 2) {
      e.preventDefault();
      const a = local(e.touches[0]);
      const b = local(e.touches[1]);
      const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const { z0, d0, m0 } = pinch;
      const s = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z0.s * (d / d0)));
      // The content point under the starting midpoint stays under the moving midpoint.
      const cx = (m0.x - z0.tx) / z0.s;
      const cy = (m0.y - z0.ty) / z0.s;
      schedule(clampZoom({ s, tx: m.x - cx * s, ty: m.y - cy * s, w: a.w, h: a.h }));
    } else if (pan && e.touches.length === 1) {
      e.preventDefault();
      const p = local(e.touches[0]);
      const { z0, p0 } = pan;
      schedule(clampZoom({ ...z0, tx: z0.tx + (p.x - p0.x), ty: z0.ty + (p.y - p0.y), w: p.w, h: p.h }));
    }
  };
  const onEnd = (e: TouchEvent) => {
    if (pinch && e.touches.length < 2) {
      pinch = null;
      // One finger left on the glass carries on as a pan where that is allowed.
      if (e.touches.length === 1 && stateRef.current.s > 1.001 && (optsRef.current.onePanAllowed?.() ?? true)) startPan(e);
    } else if (pan && e.touches.length === 0) {
      pan = null;
    }
    if (e.touches.length === 0) {
      if (raf) { cancelAnimationFrame(raf); flush(); }
      setScale(Math.round(stateRef.current.s * 10) / 10);
    }
  };
  surface.addEventListener('touchstart', onStart, { passive: false });
  surface.addEventListener('touchmove', onMove, { passive: false });
  surface.addEventListener('touchend', onEnd);
  surface.addEventListener('touchcancel', onEnd);
  /* The stage resizing (rotation, aspect settling, a panel opening) keeps
     the zoom valid rather than resetting it. */
  const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
    const r = surface.getBoundingClientRect();
    const z = stateRef.current;
    if (z.s > 1.001) apply(clampZoom({ ...z, w: r.width, h: r.height }));
    else { stateRef.current = { ...z, w: r.width, h: r.height }; optsRef.current.onChange?.(stateRef.current); }
  });
  ro?.observe(surface);
  return () => {
    if (raf) cancelAnimationFrame(raf);
    ro?.disconnect();
    surface.removeEventListener('touchstart', onStart);
    surface.removeEventListener('touchmove', onMove);
    surface.removeEventListener('touchend', onEnd);
    surface.removeEventListener('touchcancel', onEnd);
  };
}
